import { useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { ProjectTask } from "../types";
import { useLanguage } from "../contexts/LanguageContext";

interface UncompleteTaskConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: ProjectTask | null;
  onUncomplete: (task: ProjectTask) => void;
}

export default function UncompleteTaskConfirmDialog({
  open,
  onOpenChange,
  task,
  onUncomplete,
}: UncompleteTaskConfirmDialogProps) {
  // Early return before hooks if task is null
  if (!task) return null;

  const { t } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const handleUncomplete = () => {
    onUncomplete(task);
    onOpenChange(false);
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent ref={dialogContentRef}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('tasks.uncompleteTaskConfirm') || 'Mark Task as Incomplete?'}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mb-2">
              {t('tasks.uncompleteTaskDesc') || 'Are you sure you want to mark this task as incomplete?'}
            </span>
            <span className="block px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900">
              {task.name}
            </span>
            <span className="block mt-3 text-sm text-gray-600">
              {t('tasks.uncompleteTaskNote') || 'This will change the task status back to pending.'}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleUncomplete}>
            {t('tasks.uncompleteTask') || 'Mark as Incomplete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

